import { List, Map } from 'immutable'
import { createSelector } from 'reselect'
import {
  getOr,
  getIn,
  getInOr,
  getEq,
  pickToJS,
  safeToJS,
  merge,
  pick,
} from 'utils/immutable'
import { keyedObject } from 'utils/functional'
import {
  DIGITAL_INPUT,
  ANALOG_INPUT,
  CONFIGURABLE_INPUT_TYPES,
  INPUT_QUERY_TYPES,
  QUERY_INPUT_TYPES,
  CHECPLUS_MODULE_TYPES,
  CAN_INPUT,
  NATIVE_INPUT,
  BOOLEAN,
  SIMPLE,
  CHECPLUS_MODULE_NAMES,
  ACTIVE_HIGH,
  ACTIVE_LOW,
  DO_PIN_PWM,
  SWITCH_INPUT,
  VEHICLE_SUBTYPES,
} from 'utils/constants'
import { isNativeInput } from 'utils/types/features'
import findTranslations from 'utils/findTranslations'
import {
  DIGITAL_INPUT_CONFIG_DEFAULT,
  ANALOG_INPUT_CONFIG_DEFAULT,
} from 'utils/feature'

import { createToJsSelector } from 'selectors/utils'
import {
  sortBy,
  prop,
  uniq,
  complement,
  both,
  path,
  filter,
  compose,
  isNil,
  equals,
} from 'ramda'
import {
  makeLogicNodeSelector,
  availableLogicNodesMetaSelector,
} from 'selectors/feature/logicNodes'
import { getFeatureVehicleSubType } from 'selectors/feature/featureMetadata'
import { getFeatureModuleName, featureMetaDataSelector } from './shared'

import { formatSignalDescription, transformSwitchInputData } from './utils'

const signalsSelectorForModules = getIn(['feature', 'signals'])
const queriesSelector = getInOr(Map(), ['feature', 'currentFeature', 'queries'])
const conditionsSelector = getInOr(Map(), [
  'feature',
  'currentFeature',
  'conditions',
])
const scalingQueriesSelector = getInOr(Map(), [
  'feature',
  'currentFeature',
  'scalingQueries',
])
const conditionSelector = (state, { uri }) =>
  state.getIn(['feature', 'currentFeature', 'conditions', uri])
const subGroupsSelector = getIn(['feature', 'subGroups'])

const signalsSelector = createSelector(
  signalsSelectorForModules,
  getFeatureModuleName,
  (allSignals, moduleName) => {
    if (!allSignals || !moduleName) return Map()
    return allSignals.get(moduleName) || Map()
  }
)

const signalsWithTranslation = createSelector(
  signalsSelector,
  conditionsSelector,
  scalingQueriesSelector,
  (moduleSignals, conditions, scalingQueries) => {
    const queriesWithSignals = conditions.concat(scalingQueries)
    const signalsInFeature = queriesWithSignals.reduce((acc, query) => {
      const signalUri = query.get('signal')
      const inputConfig = query.get('inputConfig')

      const signal = moduleSignals.get(signalUri)
      if (!signal) return acc

      return acc.set(signalUri, findTranslations(signal, inputConfig))
    }, Map())

    return signalsInFeature
  }
)

const getSignalsWithTranslation = createToJsSelector(signalsWithTranslation)

const signalSelector = createSelector(
  signalsSelector,
  (_, props) => props.signal,
  (signals, signal) => {
    if (!signal) return Map()
    return signals.get(signal) || Map()
  }
)

const getSignal = createToJsSelector(signalSelector)
export const makeSignalSelector = () => createToJsSelector(signalSelector)

export const inputSignalSwitchTransformSelector = createSelector(
  signalsSelector,
  (_, props) => props.signal,
  (signals, signal) => {
    if (!signals || !signal) return Map()
    const signalInput = signals.get(signal)
    if (!signalInput) return Map()
    const hasSwitches = signalInput.has('switches')
    if (!hasSwitches || !signalInput) return Map()
    const switches = signalInput.get('switches')
    return switches.reduce((accum, currentSw) => {
      const panelNumber = currentSw.get('panelNumber')
      if (!panelNumber) return accum
      const lastDash = panelNumber.lastIndexOf('-')
      const suffix =
        lastDash >= 0 ? panelNumber.slice(lastDash + 1) : panelNumber
      if (!suffix) return accum
      if (accum.has(suffix)) return accum
      const switchesBySuffix = switches.filter((sw) => {
        const num = sw.get('panelNumber')
        if (!num) return false
        const d = num.lastIndexOf('-')
        return (d >= 0 ? num.slice(d + 1) : num) === suffix
      })
      return accum.set(suffix, switchesBySuffix)
    }, Map())
  }
)

export const getInputSignalSwitches = createToJsSelector(
  inputSignalSwitchTransformSelector
)

const conditionTransformSelector = createSelector(
  signalsSelector,
  (_, props) => props.conditions,
  (signals, conditions) => {
    if (!conditions) return {}
    const transformedConditions = Object.entries(conditions).reduce(
      (accum, [key, value]) => {
        // `key` here is the function query uri
        // `value` here is query object
        const { signal } = value
        const signalData = signals.get(signal)
        if (getOr(List(), 'switches', signalData).size > 0) {
          return {
            ...accum,
            [key]: {
              ...value,
              subType: NATIVE_INPUT,
            },
          }
        }
        return { ...accum, [key]: value }
      },
      {}
    )
    return safeToJS(transformedConditions)
  }
)

const signalTranslationSelector = createSelector(
  signalSelector,
  (_, { inputConfig }) => inputConfig,
  findTranslations
)

const getSignalTranslation = createToJsSelector(signalTranslationSelector)

const getSignalDescription = createSelector(signalSelector, (signal) =>
  signal ? signal.get('description') : null
)

const getSignalOrFloatingConfig = (subType, signal) => {
  if (!signal && subType === DIGITAL_INPUT) return DIGITAL_INPUT_CONFIG_DEFAULT
  if (!signal && subType === ANALOG_INPUT) return ANALOG_INPUT_CONFIG_DEFAULT

  return signal
}

// TODO: refactor to use immutables only -- DO THIS BEFORE MERGE
const getConfiguredInputs = createSelector(
  createToJsSelector(signalsSelector),
  createToJsSelector(conditionsSelector),
  (signalsMap, conditions) => {
    if (!signalsMap || !conditions) {
      return {}
    }

    const configurableSignals = Object.values(signalsMap).filter(
      (s) => s.dataType === DIGITAL_INPUT || s.dataType === ANALOG_INPUT
    )
    const configurableSignalUris = new Set(
      configurableSignals.map((s) => s.uri)
    )

    return (
      Object.values(conditions)
        // find all the simple queries with digital inputs:
        .filter(
          (condition) =>
            condition &&
            (configurableSignalUris.has(condition.signal) ||
              !isNil(condition.floatingCircuit))
        )
        // reduce to a map of signal-ids to configured values:
        .reduce((acc, condition) => {
          const { subType } = condition

          const key = condition.signal || condition.floatingCircuit
          // only add the signal as a key if it isn't there already:

          if (!acc[key]) {
            const signal = signalsMap[key]
            const signalOrFloatingConfig = getSignalOrFloatingConfig(
              subType,
              signal
            )

            const { description = '', dataType } = signalOrFloatingConfig || {}
            acc[key] = {
              conditionsSharingConfig: [condition.uri],
              inputConfig: condition.inputConfig,
              description,
              dataType, // TODO: can we just use subType?
            }
          } else {
            acc[key].conditionsSharingConfig.push(condition.uri)

            if (!acc[key].inputConfig) {
              acc[key].inputConfig = condition.inputConfig
            }
          }

          return acc
        }, {})
    )
  }
)

const selectSignalUris = createSelector(signalsSelector, (signals) => {
  if (!signals) return List()
  return signals && signals.keySeq()
})

const getSignalUris = safeToJS(selectSignalUris)

const getDefaultInputConfigurations = createSelector(
  createToJsSelector(signalsSelector),
  (signals) =>
    signals
      ? Object.values(signals)
          .filter((signal) => CONFIGURABLE_INPUT_TYPES.has(signal.dataType))
          .reduce(
            (obj, signal) =>
              Object.assign(obj, {
                [signal.uri]: signal.defaultInputConfig || signal.defaultValue,
              }),
            {}
          )
      : null
)

const getSignalInputType = createSelector(
  signalSelector,
  (signal) => signal.get('dataType') || QUERY_INPUT_TYPES.CARRY
)

const makeGetSignalInputType = () => getSignalInputType

// TODO: talk to adi about getting this info on the backend
const mapQuery = (query) => query.set('inputType', QUERY_INPUT_TYPES.CARRY)

const mapSignal = (signal) => {
  const transform = compose(
    merge({
      inputType: signal.get('dataType'),
      description: formatSignalDescription(signal),
    }),
    pick(['uri', 'translationType'])
  )

  return transform(signal)
}

// combine 3 position switches into single value pairs, 2 position keep as is
// return both 3 positon and 2 position switches
// customDescriptionMap: Immutable Map of signalUri → customDescription (for pill display)
export const filteredSwitchSignalInputs = (
  signals,
  customDescriptionMap = Map()
) => {
  if (!signals) {
    return null
  }
  const switchSignals = signals.filter(
    (signal) =>
      getEq('dataType', NATIVE_INPUT)(signal) &&
      getOr(List(), 'switches', signal).size > 0
  )
  const optionsBySwitchSignals = switchSignals.reduce((acc, switchSignal) => {
    if (!switchSignal) {
      return acc
    }
    // collect 2 position switches
    const {
      description,
      uri,
      relatedInputs,
      interface: switchPositions,
    } = pickToJS(
      ['description', 'uri', 'relatedInputs', 'interface'],
      switchSignal
    )
    const strippedDescription = description
      ? description.replace(' Request', '')
      : description
    if (isNil(relatedInputs)) {
      const customDescription = customDescriptionMap.get(uri) || null
      const transformedTwoPostionOption = Map({
        uriOptionsByPosition: [
          { value: uri, position: 'up' },
          { value: null, position: 'down' },
        ],
        description: strippedDescription,
        uri,
        inputType: SWITCH_INPUT,
        customDescription,
      })
      return acc.push(transformedTwoPostionOption)
    }
    // collect 3 position switches
    // Identify the UP signal: either switch_up === 1 (legacy) or switch_down !== 1
    // Only add the UP signal to avoid duplicate entries per switch pair
    const relatedInputUri = Object.keys(relatedInputs)[0]
    const positionUp = isNil(relatedInputUri)
      ? null
      : path(['switchPositionDictionary', 'switch_up'], switchPositions)
    const positionDown = isNil(relatedInputUri)
      ? null
      : path(['switchPositionDictionary', 'switch_down'], switchPositions)
    const isUpSignal = equals(positionUp, 1) || !equals(positionDown, 1)
    let transformedThreePostionOption = transformSwitchInputData(switchSignal)
    // Strip " Request" from description
    const rawDesc = transformedThreePostionOption.get('description')
    transformedThreePostionOption = transformedThreePostionOption.set(
      'description',
      rawDesc ? rawDesc.replace(' Request', '') : rawDesc
    )
    // Resolve customDescription — check the main URI and both Up/Down position URIs
    const uriOptionsByPosition =
      transformedThreePostionOption.get('uriOptionsByPosition') || []
    const allUris = [
      transformedThreePostionOption.get('uri'),
      ...uriOptionsByPosition.map((p) => p.value).filter(Boolean),
    ]
    const customDescription = allUris.reduce(
      (found, u) => found || customDescriptionMap.get(u) || null,
      null
    )
    transformedThreePostionOption = transformedThreePostionOption.set(
      'customDescription',
      customDescription
    )
    if (isUpSignal) return acc.push(transformedThreePostionOption)
    return acc
  }, List())
  return optionsBySwitchSignals
}

const mappedInputOptions = createSelector(
  signalsSelector,
  availableLogicNodesMetaSelector,
  getFeatureVehicleSubType,
  conditionsSelector,
  (signals, queries, vehicleSubType, conditions) => {
    if (!signals || !queries) return null
    const mappedQueries = queries.map(mapQuery)
    const signalsForBus = signals.filter((signal) =>
      getEq('vehicleSubType', VEHICLE_SUBTYPES.BUS)(signal)
    )
    const signalsForNonBus = signals.filter(
      (signal) => !getEq('vehicleSubType', VEHICLE_SUBTYPES.BUS)(signal)
    )
    const isBusTypeFeature = vehicleSubType === VEHICLE_SUBTYPES.BUS
    const signalsByVehicleSubtype = isBusTypeFeature
      ? signalsForBus
      : signalsForNonBus
    // Build signalUri → customDescription map from current feature conditions
    const customDescriptionMap = conditions.reduce((acc, condition) => {
      const signalUri = condition.get('signal')
      const customDesc = condition.get('customDescription')
      if (signalUri && customDesc) {
        return acc.set(signalUri, customDesc)
      }
      return acc
    }, Map())
    const filteredSwitchSignals = filteredSwitchSignalInputs(
      signalsByVehicleSubtype,
      customDescriptionMap
    )
    const nonSwitchSignals = signals.filter(
      (signal) =>
        !(
          getEq('dataType', NATIVE_INPUT)(signal) &&
          getOr(List(), 'switches', signal).size > 0
        )
    )
    const mappedSignals = nonSwitchSignals
      .valueSeq()
      .map((signal) =>
        mapSignal(signal).set(
          'customDescription',
          customDescriptionMap.get(signal.get('uri')) || null
        )
      )
    // filter and reduce to a list of all other inputs + switch input signal uris(by vehicle subtype)
    const allSignals = mappedSignals.concat(filteredSwitchSignals)
    return mappedQueries.concat(allSignals)
  }
)

const getNotEq = complement(getEq)
const isBooleanAndNativeOption = both(
  getEq('translationType', BOOLEAN),
  getEq('inputType', NATIVE_INPUT)
)
const isNotBooleanAndNativeOption = both(
  getNotEq('translationType', BOOLEAN),
  getEq('inputType', NATIVE_INPUT)
)

const nonBooleanNativeInputOptions = filter(isNotBooleanAndNativeOption)
const booleanNativeInputOptions = filter(isBooleanAndNativeOption)
const inputOptionsFilteredSelector = createSelector(
  mappedInputOptions,
  (_, props) => props,
  (
    options,
    {
      isSixteenBitOutput,
      hasNativeOutput,
      subType,
      isSingleCondition,
      isOutputBoolean,
      outputConfig,
    }
  ) => {
    const updateSubtype = isNil(subType) ? NATIVE_INPUT : subType
    const inputTypeFilter = filter(getEq('inputType', updateSubtype))
    // boolean and non-boolean options should be filtered for native outputs driven by single conditions
    // logic sets and timers will have all input options by input subtype
    if (
      hasNativeOutput &&
      isNativeInput(updateSubtype) &&
      isSingleCondition &&
      isOutputBoolean
    ) {
      return booleanNativeInputOptions(options)
    }
    const filterForNonBooleanOutputOptions = () => {
      const validOutputType =
        isSixteenBitOutput ||
        (hasNativeOutput && !isOutputBoolean) ||
        outputConfig === DO_PIN_PWM
      const validInputType = isNativeInput(updateSubtype)
      return validOutputType && validInputType && isSingleCondition
    }
    if (filterForNonBooleanOutputOptions()) {
      // TODO: can I just pass nonBooleanNatives prop to sixteenBitOutputs ? (remove isSixteenBitOutput)
      return nonBooleanNativeInputOptions(options)
    }
    return inputTypeFilter(options)
  }
)

const getInputOptionsFiltered = createToJsSelector(inputOptionsFilteredSelector)

// Factory that creates per-instance selector to avoid shared memoization issues.
// Each component instance gets its own selector with independent cache.
// IMPORTANT: This creates a fresh mappedInputOptions for each instance to ensure
// new API data propagates correctly through the entire selector chain.
const makeGetInputOptionsFiltered = () => {
  // Create per-instance signalsSelector to avoid shared memoization
  const instanceSignalsSelector = createSelector(
    signalsSelectorForModules,
    getFeatureModuleName,
    (allSignals, moduleName) => {
      if (!allSignals || !moduleName) return Map()
      return allSignals.get(moduleName) || Map()
    }
  )

  // Create per-instance mappedInputOptions
  const instanceMappedInputOptions = createSelector(
    instanceSignalsSelector,
    availableLogicNodesMetaSelector,
    getFeatureVehicleSubType,
    conditionsSelector,
    (signals, queries, vehicleSubType, conditions) => {
      if (!signals || !queries) return null
      const mappedQueries = queries.map(mapQuery)
      const signalsForBus = signals.filter((signal) =>
        getEq('vehicleSubType', VEHICLE_SUBTYPES.BUS)(signal)
      )
      const signalsForNonBus = signals.filter(
        (signal) => !getEq('vehicleSubType', VEHICLE_SUBTYPES.BUS)(signal)
      )
      const isBusTypeFeature = vehicleSubType === VEHICLE_SUBTYPES.BUS
      const signalsByVehicleSubtype = isBusTypeFeature
        ? signalsForBus
        : signalsForNonBus
      const customDescriptionMap = conditions.reduce((acc, condition) => {
        const signalUri = condition.get('signal')
        const customDesc = condition.get('customDescription')
        if (signalUri && customDesc) {
          return acc.set(signalUri, customDesc)
        }
        return acc
      }, Map())
      const filteredSwitchSignals = filteredSwitchSignalInputs(
        signalsByVehicleSubtype,
        customDescriptionMap
      )
      const nonSwitchSignals = signals.filter(
        (signal) =>
          !(
            getEq('dataType', NATIVE_INPUT)(signal) &&
            getOr(List(), 'switches', signal).size > 0
          )
      )
      const mappedSignals = nonSwitchSignals
        .valueSeq()
        .map((signal) =>
          mapSignal(signal).set(
            'customDescription',
            customDescriptionMap.get(signal.get('uri')) || null
          )
        )
      const allSignals = mappedSignals.concat(filteredSwitchSignals)
      return mappedQueries.concat(allSignals)
    }
  )

  // Create a new filtered selector instance for this component
  const instanceFilteredSelector = createSelector(
    instanceMappedInputOptions,
    (_, props) => props,
    (
      options,
      {
        isSixteenBitOutput,
        hasNativeOutput,
        subType,
        isSingleCondition,
        isOutputBoolean,
        outputConfig,
      }
    ) => {
      const updateSubtype = isNil(subType) ? NATIVE_INPUT : subType
      const inputTypeFilter = filter(getEq('inputType', updateSubtype))
      if (
        hasNativeOutput &&
        isNativeInput(updateSubtype) &&
        isSingleCondition &&
        isOutputBoolean
      ) {
        return booleanNativeInputOptions(options)
      }
      const filterForNonBooleanOutputOptions = () => {
        const validOutputType =
          isSixteenBitOutput ||
          (hasNativeOutput && !isOutputBoolean) ||
          outputConfig === DO_PIN_PWM
        const validInputType = isNativeInput(updateSubtype)
        return validOutputType && validInputType && isSingleCondition
      }
      if (filterForNonBooleanOutputOptions()) {
        return nonBooleanNativeInputOptions(options)
      }
      return inputTypeFilter(options)
    }
  )
  // Create per-instance toJS wrapper with its own memoization cache
  return createToJsSelector(instanceFilteredSelector)
}

const getInputDescriptions = createSelector(
  signalsSelector,
  queriesSelector,
  (signals, queries) => {
    if (!signals || !queries) {
      return null
    }
    const picker = pickToJS(['uri', 'description'])
    const mappedQueries = queries
      .valueSeq()
      .filter((query) => query && INPUT_QUERY_TYPES.has(query.get('queryType')))
      .map(picker)
      .toJS()
    const sortedMappedQueries = sortBy(prop('description'), mappedQueries)
    const nonSwitchSignals = signals.filter(
      (signal) =>
        !(
          getEq('dataType', NATIVE_INPUT)(signal) &&
          getOr(List(), 'switches', signal).size > 0
        )
    )
    const switchSignals = filteredSwitchSignalInputs(signals)
    const mappedSignals = nonSwitchSignals.valueSeq().map(picker).toJS()
    const mappedSwitchSignals = switchSignals.map(picker).toJS()
    const merged = [
      ...sortedMappedQueries,
      ...mappedSignals,
      ...mappedSwitchSignals,
    ]

    return keyedObject('uri', merged)
  }
)

const findQuerySignal = (query, signals) => {
  const signalUri = query && query.signal
  if (signalUri) {
    const signal = signals.get(signalUri)
    if (signal) {
      return signal.toJS()
    }
  }
  return null
}

const makeQuerySignalSelector = () => {
  const logicNodeSelector = makeLogicNodeSelector()
  return createSelector(logicNodeSelector, signalsSelector, findQuerySignal)
}

const analogSignalsSelector = createSelector(signalsSelector, (signals) =>
  signals
    .filter(
      (signal) =>
        signal.get('dataType') === ANALOG_INPUT &&
        signal.get('moduleType') === CHECPLUS_MODULE_TYPES.XMC
    )
    .toList()
    .sortBy((signal) => signal.get('description'))
    .toJS()
)

const featureCanIdsSelector = createSelector(featureMetaDataSelector, (value) =>
  getOr(List(), 'canIds', value)
)

const getCanIds = createSelector(
  signalsSelector,
  featureCanIdsSelector,
  queriesSelector,
  (signals, featureCanIds, queries) => {
    const universalCanIds = signals
      .filter((signal) => signal.get('dataType') === CAN_INPUT)
      .valueSeq()
      .map((signal) => signal.get('id'))

    const queryCanIds = queries
      .filter((query) => query.has('canId'))
      .valueSeq()
      .map((query) => query.get('canId'))

    const list = universalCanIds.concat(featureCanIds, queryCanIds)
    return uniq(list.sort().toJS())
  }
)

const getSubGroups = createToJsSelector(subGroupsSelector)

/**
 * RULES for disabeling Config buttons
 *
 * ASAM, DIGITAL_INPUT, D1 -> LOW disabled
 * ASAM, DIGITAL_INPUT, D2 -> HIGH disabled
 */

const dictionary = {
  [CHECPLUS_MODULE_NAMES.ASAM]: {
    [DIGITAL_INPUT]: {
      '/signals/SSAM_160': [ACTIVE_LOW], // Digital Input 1
      '/signals/SSAM_161': [ACTIVE_HIGH], // Digital Input 2
    },
  },
}
const getDisabledConfigFields = createSelector(
  featureMetaDataSelector,
  conditionSelector,
  (feature, condition = Map()) => {
    const module = feature.get('moduleName')
    const signalUri = condition.get('signal')
    const type = condition.get('queryType')
    const conditionType = type === SIMPLE ? condition.get('subType') : type

    return path([module, conditionType, signalUri], dictionary) || []
  }
)

export {
  signalsSelector,
  getInputDescriptions,
  getSignal,
  getSignalTranslation,
  getSignalDescription,
  getConfiguredInputs,
  getDefaultInputConfigurations,
  makeQuerySignalSelector,
  analogSignalsSelector,
  getCanIds,
  getSubGroups,
  getDisabledConfigFields,
  getSignalUris,
  signalsWithTranslation,
  getSignalsWithTranslation,
  getInputOptionsFiltered,
  mappedInputOptions,
  makeGetInputOptionsFiltered,
  inputOptionsFilteredSelector,
  makeGetSignalInputType,
  conditionTransformSelector,
}
